import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";

/* ── Icons ──────────────────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconLayers   = () => <Icon size={22} d={["M12 2 2 7l10 5 10-5-10-5z", "M2 17l10 5 10-5", "M2 12l10 5 10-5"]} />;
const IconPlus     = () => <Icon size={14} stroke={2.5} d={["M12 5v14", "M5 12h14"]} />;
const IconSearch   = () => <Icon size={16} d={["M21 21l-4.35-4.35", "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"]} />;
const IconEdit     = () => <Icon size={14} d={["M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7", "M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"]} />;
const IconPause    = () => <Icon size={14} d={["M6 4h4v16H6z", "M14 4h4v16h-4z"]} />;
const IconBuilding = () => <Icon size={14} d={["M4 22V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v18", "M9 22v-4h6v4", "M9 8h.01", "M15 8h.01", "M9 12h.01", "M15 12h.01"]} />;
const IconUsers    = () => <Icon size={14} d={["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"]} />;
const IconBox      = () => <Icon size={14} d={["M21 8a1 1 0 0 0-.5-.87l-8-4.62a1 1 0 0 0-1 0l-8 4.62A1 1 0 0 0 3 8v8a1 1 0 0 0 .5.87l8 4.62a1 1 0 0 0 1 0l8-4.62A1 1 0 0 0 21 16z", "M3.27 6.96 12 12.01l8.73-5.05", "M12 22.08V12"]} />;
const IconInbox    = () => <Icon size={36} d={["M22 12h-6l-2 3H10l-2-3H2", "M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"]} />;

const TYPE_TONE = {
  BASIC:      { bg: "rgba(100,116,139,0.12)", fg: "#475569" },
  PRO:        { bg: "rgba(99,102,241,0.12)",  fg: "#4f46e5" },
  ENTERPRISE: { bg: "rgba(139,92,246,0.12)",  fg: "#7c3aed" },
};

/* ── Scoped CSS ─────────────────────────────────────────── */
const PAGE_CSS = `
  .pl-page {
    min-height: 100vh;
    background: #f4f5f8;
    color: #1a1c26;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-size: 13px;
    padding: 32px 36px;
  }
  .pl-page__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
  }
  .pl-page__title {
    font-size: 22px;
    font-weight: 700;
    color: #14161f;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .pl-page__title svg { color: #6366f1; }
  .pl-page__subtitle {
    font-size: 13px;
    color: rgba(20,22,31,0.45);
    margin-top: 6px;
  }

  /* Filters */
  .pl-filters {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
    align-items: center;
  }
  .pl-search {
    position: relative;
    flex: 1;
    min-width: 240px;
  }
  .pl-search svg {
    position: absolute;
    left: 14px; top: 50%;
    transform: translateY(-50%);
    color: rgba(20,22,31,0.3);
  }
  .pl-search input {
    width: 100%;
    padding: 10px 14px 10px 40px;
    background: #fff;
    border: 1px solid rgba(20,22,31,0.1);
    border-radius: 10px;
    color: #1a1c26;
    font-size: 13px;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .pl-search input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }
  .pl-chip-group { display: inline-flex; gap: 6px; flex-wrap: wrap; }
  .pl-chip {
    border: 1px solid rgba(20,22,31,0.1);
    background: #fff;
    color: rgba(20,22,31,0.55);
    border-radius: 20px;
    padding: 7px 16px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .pl-chip:hover { border-color: rgba(99,102,241,0.35); color: #6366f1; }
  .pl-chip--active {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-color: transparent;
    color: #fff;
  }

  /* Grid */
  .pl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  /* Card */
  .pl-card {
    background: #fff;
    border: 1px solid rgba(20,22,31,0.08);
    border-radius: 16px;
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 1px 2px rgba(20,22,31,0.04);
    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  }
  .pl-card:hover {
    border-color: rgba(99,102,241,0.3);
    box-shadow: 0 12px 28px rgba(20,22,31,0.08);
    transform: translateY(-2px);
  }
  .pl-card--inactive { opacity: 0.6; }

  .pl-card__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  .pl-card__name {
    font-size: 18px;
    font-weight: 700;
    color: #14161f;
    letter-spacing: -0.01em;
    margin: 0 0 6px;
  }
  .pl-type-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
  .pl-status-dot {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: rgba(20,22,31,0.5);
  }
  .pl-status-dot::before {
    content: '';
    width: 7px; height: 7px; border-radius: 50%;
    background: currentColor;
  }
  .pl-status-dot--active { color: #1dab63; }
  .pl-status-dot--inactive { color: #e0364f; }

  .pl-card__desc {
    font-size: 12px;
    color: rgba(20,22,31,0.5);
    line-height: 1.5;
    margin: 0;
  }

  .pl-price {
    padding: 16px 0;
    border-top: 1px solid rgba(20,22,31,0.08);
    border-bottom: 1px solid rgba(20,22,31,0.08);
  }
  .pl-price__main {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }
  .pl-price__amount {
    font-size: 28px;
    font-weight: 800;
    color: #14161f;
    letter-spacing: -0.02em;
  }
  .pl-price__period {
    font-size: 12px;
    color: rgba(20,22,31,0.45);
  }
  .pl-price__yearly {
    margin-top: 4px;
    font-size: 12px;
    color: rgba(20,22,31,0.45);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .pl-save-badge {
    font-size: 10px;
    font-weight: 700;
    color: #1dab63;
    background: rgba(29,171,99,0.12);
    padding: 2px 8px;
    border-radius: 10px;
  }

  .pl-limits { display: flex; flex-direction: column; gap: 10px; }
  .pl-limit-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: rgba(20,22,31,0.55);
  }
  .pl-limit-row svg { color: rgba(20,22,31,0.3); flex-shrink: 0; }
  .pl-limit-row strong {
    margin-left: auto;
    color: #14161f;
    font-family: var(--font-mono, monospace);
  }

  .pl-card__actions {
    display: flex;
    gap: 8px;
    margin-top: auto;
  }

  /* Buttons */
  .pl-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    padding: 9px 14px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .pl-btn--edit {
    background: rgba(20,22,31,0.04);
    border: 1px solid rgba(20,22,31,0.1);
    color: rgba(20,22,31,0.7);
  }
  .pl-btn--edit:hover {
    border-color: #6366f1;
    color: #6366f1;
    background: rgba(99,102,241,0.06);
  }
  .pl-btn--deactivate {
    background: rgba(224,54,79,0.08);
    border: 1px solid rgba(224,54,79,0.25);
    color: #e0364f;
  }
  .pl-btn--deactivate:hover {
    background: rgba(224,54,79,0.16);
    color: #c22841;
  }
  .pl-btn--add {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff;
    box-shadow: 0 4px 12px rgba(99,102,241,0.25);
  }
  .pl-btn--add:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99,102,241,0.35);
  }

  /* Empty / loading */
  .pl-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    color: rgba(20,22,31,0.25);
  }
  .pl-empty svg { margin-bottom: 16px; opacity: 0.5; }
  .pl-empty p { font-size: 14px; color: rgba(20,22,31,0.4); margin: 0 0 16px; }
`;

const PlansList = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  useEffect(() => {
    const styleId = "pl-page-css";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent = PAGE_CSS;
      document.head.appendChild(el);
    }
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/api/plans");
      setPlans(response.data);
    } catch (err) {
      toast.error("Failed to load plans");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleDeactivate = async (id) => {
    try {
      await axiosPrivate.patch(`/api/plans/${id}/deactivate`);
      toast.success("Plan deactivated");
      fetchPlans();
    } catch (_) {
      toast.error("Could not deactivate plan");
    }
  };

  const types = useMemo(() => ["ALL", ...new Set(plans.map((p) => p.type))], [plans]);

  const filteredPlans = useMemo(() => {
    return plans.filter((p) => {
      const matchesSearch = !search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase());
      const matchesType = typeFilter === "ALL" || p.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [plans, search, typeFilter]);

  return (
    <div className="pl-page mt-4">
      <div className="pl-page__header">
        <div>
          <div className="pl-page__title"><IconLayers /> Plans</div>
          <div className="pl-page__subtitle">Subscription tiers available on the platform</div>
        </div>
        <button className="pl-btn pl-btn--add" style={{ flex: "none" }} onClick={() => navigate("/admin/add-plan")}>
          <IconPlus /> Add plan
        </button>
      </div>

      <div className="pl-filters">
        <div className="pl-search">
          <IconSearch />
          <input
            placeholder="Search plans…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="pl-chip-group">
          {types.map((t) => (
            <button
              key={t}
              className={`pl-chip ${typeFilter === t ? "pl-chip--active" : ""}`}
              onClick={() => setTypeFilter(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="pl-empty"><p>Loading plans…</p></div>
      ) : filteredPlans.length === 0 ? (
        <div className="pl-empty">
          <IconInbox />
          <p>{plans.length === 0 ? "No plans yet — create the first one to start onboarding tenants." : "No plans match your filters."}</p>
          {plans.length === 0 && (
            <button className="pl-btn pl-btn--add" style={{ flex: "none" }} onClick={() => navigate("/admin/add-plan")}>
              <IconPlus /> Add plan
            </button>
          )}
        </div>
      ) : (
        <div className="pl-grid">
          {filteredPlans.map((plan) => {
            const tone = TYPE_TONE[plan.type] || { bg: "rgba(148,163,184,0.14)", fg: "#94a3b8" };
            const savingsPct = plan.monthlyPrice && plan.yearlyPrice
              ? Math.round((1 - (plan.yearlyPrice / 12) / plan.monthlyPrice) * 100)
              : 0;

            return (
              <div key={plan.id} className={`pl-card ${!plan.active ? "pl-card--inactive" : ""}`}>
                <div className="pl-card__head">
                  <div>
                    <h3 className="pl-card__name">{plan.name}</h3>
                    <span className="pl-type-badge" style={{ background: tone.bg, color: tone.fg }}>
                      {plan.type}
                    </span>
                  </div>
                  <span className={`pl-status-dot ${plan.active ? "pl-status-dot--active" : "pl-status-dot--inactive"}`}>
                    {plan.active ? "Active" : "Inactive"}
                  </span>
                </div>

                {plan.description && <p className="pl-card__desc">{plan.description}</p>}

                <div className="pl-price">
                  <div className="pl-price__main">
                    <span className="pl-price__amount">{plan.currency} {Number(plan.monthlyPrice).toFixed(2)}</span>
                    <span className="pl-price__period">/ month</span>
                  </div>
                  <div className="pl-price__yearly">
                    <span>or {plan.currency} {Number(plan.yearlyPrice).toFixed(2)} / year</span>
                    {savingsPct > 0 && <span className="pl-save-badge">save {savingsPct}%</span>}
                  </div>
                </div>

                <div className="pl-limits">
                  <div className="pl-limit-row"><IconBuilding /> Branches <strong>{plan.maxBranches}</strong></div>
                  <div className="pl-limit-row"><IconUsers /> Users / branch <strong>{plan.maxUsersPerBranch}</strong></div>
                  <div className="pl-limit-row"><IconBox /> Products / branch <strong>{plan.maxProductsPerBranch}</strong></div>
                </div>

                <div className="pl-card__actions">
                  <button className="pl-btn pl-btn--edit" onClick={() => navigate(`/admin/edit-plan/${plan.id}`)}>
                    <IconEdit /> Edit
                  </button>
                  {plan.active && (
                    <button className="pl-btn pl-btn--deactivate" onClick={() => handleDeactivate(plan.id)}>
                      <IconPause /> Deactivate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlansList;